// src/components/GlobalCORSModal.jsx - FIXED FOR FIREFOX
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box
} from '@mui/material';
import CircularTMSLoader from '../../TmsLoader/TmsLoader';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { addApiStatusListener, isApiCurrentlyDown } from '../../apis/Agency';

const GlobalCORSModal = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const errorTimeoutRef = useRef(null);

  const isPublicRoute = () => {
    const publicRoutes = ['/agency-resetpassword', '/reset-password'];
    return publicRoutes.some(
      route =>
        location.pathname === route || location.pathname.startsWith('/reset')
    );
  };

  useEffect(() => {
    if (isPublicRoute()) {
      setOpen(false);
      return;
    }

    const unsubscribe = addApiStatusListener(error => {
      if (isPublicRoute()) return;

      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      if (error) {
        // Add a delay before showing modal (500ms)
        // This prevents modal from showing on brief network glitches during page load
        errorTimeoutRef.current = setTimeout(() => {
          // Double-check if API is still down after delay
          if (isApiCurrentlyDown()) {
            setOpen(true);
            setIsRecovering(false);
          }
        }, 500);
      } else {
        setOpen(false);
        setIsRecovering(true);
        setTimeout(() => {
          setOpen(false);
          window.location.reload();
        }, 2000);
      }
    });

    // Don't show modal immediately on page load
    // Give it a moment to see if API recovers
    const initialCheck = setTimeout(() => {
      if (!isPublicRoute() && isApiCurrentlyDown()) {
        setOpen(true);
      }
    }, 2000);

    return () => {
      unsubscribe();
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      clearTimeout(initialCheck);
    };
  }, [location.pathname]);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (isPublicRoute()) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth='sm'
      fullWidth
      style={{ zIndex: 999999 }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        {isRecovering ? (
          <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
        ) : (
          <CloudOffIcon sx={{ fontSize: 60, color: '#f44336' }} />
        )}
        <Typography variant='h5' fontWeight='bold'>
          {isRecovering ? 'System Recovery' : 'System Under Maintenance'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {isRecovering ? (
            <>
              <CircularTMSLoader size={60} variant='progress' />
              <Typography color='success.main'>
                API services are back online! Refreshing...
              </Typography>
            </>
          ) : (
            <>
              <CircularTMSLoader size={60} variant='progress' />
              <Typography>System is currently being updated.</Typography>
              <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                Please wait while we restore services...
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        {!isRecovering && (
          <Button variant='contained' onClick={handleRefresh}>
            Refresh Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GlobalCORSModal;
